import { BusIcon } from '@phosphor-icons/react';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import {
  arrivalDayOffset,
  fmtTime,
  fmtWeekdayDayMonth,
  nextDayArrivalLabel,
} from '../../../../shared/ui/flight_format.js';
import { useGenUiI18n, type GenUiTranslate } from '../../../../shared/ui/i18n.js';
import type { CheckoutItem } from '@voll-intelligence/genui-contract';
import { CheckoutTripLeg, CheckoutTripLegs, StopsPill } from '../../ui/checkout_trip_leg.js';
import {
  fmtDurationMinutes,
  seatsText,
  type CheckoutBusCopy,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useCheckoutEnv } from '../../core/checkout_env.js';

export type BusCheckoutItem = Extract<CheckoutItem, { productType: 'bus' }>;

/** "Poltrona 12" / "sem assento" num trecho só; com conexão, cada trecho com o
 *  seu rótulo ("Trecho 1: 12 · Trecho 2: sem assento"), como o detalhe expandido —
 *  o resumo não esconde o trecho sem poltrona nem de que trecho é cada uma. */
function seatsLine(
  seats: readonly string[][] | undefined,
  t: GenUiTranslate,
  bus: CheckoutBusCopy,
): string | null {
  if (!seats || seats.length === 0) return null;
  if (seats.length > 1) return seatsText(seats, bus);
  const codes = seats.flat();
  if (codes.length === 0) return t('busSeatMap.noSeatSelected');
  return t(codes.length === 1 ? 'productCheckout.bus.seat_one' : 'productCheckout.bus.seat_other', {
    seats: codes.join(', '),
  });
}

/** A linha sob o total do ônibus (frame 07): ida e volta ou só ida, por
 *  pessoa — o total do ônibus é o de quem conversa, a única pessoa da reserva. */
export function busTotalNote(items: readonly BusCheckoutItem[], t: GenUiTranslate): string {
  return items.some((item) => item.detail.leg === 'return')
    ? t('productCheckout.bus.totalRoundTrip')
    : t('productCheckout.bus.totalOneWay');
}

/** A viação no cabeçalho: o lockup horizontal da logo (64 × 20, não o quadrado
 *  do aéreo) ou, sem logo ou com a imagem quebrada, o nome escrito; a classe
 *  segue depois. */
function BusOperator({
  name,
  logoUrl,
  seatClass,
}: {
  readonly name: string | undefined;
  readonly logoUrl: string | undefined;
  readonly seatClass: string | undefined;
}) {
  const logo = useImageFallback(logoUrl);
  if (logo.src && name) {
    return (
      <>
        <img
          src={logo.src}
          alt={name}
          onError={logo.onError}
          className="h-5 w-16 shrink-0 object-contain object-right"
        />
        {seatClass && <span className="truncate">{seatClass}</span>}
      </>
    );
  }
  const text = [name, seatClass].filter(Boolean).join(' · ');
  return text ? <span className="truncate">{text}</span> : null;
}

function BusCheckoutLeg({ item }: { readonly item: BusCheckoutItem }) {
  const { t, locale } = useGenUiI18n();
  const { bus } = useCheckoutEnv().copy;
  const { detail } = item;
  const { departure, arrival } = detail;
  const date = fmtWeekdayDayMonth(departure.dateTime, locale);
  // Item sem perna é a viagem só de ida.
  const direction = t(`busOffers.${detail.leg ?? 'outbound'}`);
  const days = arrivalDayOffset(departure.dateTime, arrival.dateTime);
  const connections = detail.connections ?? 0;
  const rule = [detail.seatClass, seatsLine(detail.seats, t, bus)].filter(Boolean).join(' · ');
  return (
    <div role="group" aria-label={`${direction} · ${date}`}>
      <CheckoutTripLeg
        icon={<BusIcon size={16} weight="bold" />}
        date={date}
        direction={direction}
        operator={
          detail.carrierName || detail.seatClass ? (
            <BusOperator
              name={detail.carrierName}
              logoUrl={detail.carrierLogoUrl}
              seatClass={detail.seatClass}
            />
          ) : undefined
        }
        // Rodoviária não tem IATA, e o item não traz a sigla da cidade: a
        // rodoviária vai inteira na linha de baixo.
        departure={{ time: fmtTime(departure.dateTime), place: departure.stopName }}
        arrival={{ time: fmtTime(arrival.dateTime), place: arrival.stopName }}
        arrivalDayOffset={{ days, label: nextDayArrivalLabel(days, locale) }}
        rail={
          connections > 0 ? (
            <StopsPill>
              {t(connections === 1 ? 'busOffers.connection_one' : 'busOffers.connection_other', {
                count: connections,
              })}
            </StopsPill>
          ) : (
            <>
              <BusIcon size={14} aria-hidden />
              <span className="sr-only">{t('common.direct')}</span>
            </>
          )
        }
        duration={
          detail.durationMinutes != null ? fmtDurationMinutes(detail.durationMinutes) : undefined
        }
        rule={rule || undefined}
      />
    </div>
  );
}

/**
 * Corpo de ônibus do card `product_checkout` — o «Trecho do ônibus» do Figma
 * final do 1click (#5283, 17/10): uma perna por item, ida e volta lado a lado
 * quando o card é largo, com a mesma gramática do trecho do voo. O preço mora
 * no total do rodapé.
 */
export function BusCheckoutLegs({ items }: Readonly<{ items: readonly BusCheckoutItem[] }>) {
  return (
    <CheckoutTripLegs pair={items.length === 2}>
      {items.map((item, index) => (
        <BusCheckoutLeg key={`${item.offerId}:${String(index)}`} item={item} />
      ))}
    </CheckoutTripLegs>
  );
}
